import * as commonConsts from '../../pages/common/CommonConsts';
import { getOrgApplicationDetails, getOrgAuthorityHolderDetails } from './Helper';
import {
  getNewIndividualTrader,
  getDuplicateAddressDetail,
  getNewOrganisation,
  getMultipleAddressDetail,
  getNewContactPerson,
  isPrimaryContact,
  getNewIndividualAccountant,
  getNewOrganisationAccountant,
  getNewLeadAuditor
} from '../../utils/Helper';
import { getAllDocuments } from '../../utils/SupportingDocumentHelper';
import moment from 'moment';

const formattedOrganisationNewApplicationJson = (application, newApplicationDetail, twoFA) => {
  const {
    organisationPersonalDetail,
    validationContactInformation,
    supportingDocument,
    duration,
    trader,
    branch,
    organisationLicence,
    organisationAuthorityHolder,
    fundraisingDetail,
    accountant
  } = newApplicationDetail;
  const { data } = twoFA;
  const { newTraders } = trader;
  const { newBranches } = branch;
  const { orgDataInfo } = organisationLicence;
  const {
    otherAddresses,
    addressRegisteredOfficeValue,
    sameAsRegisteredOffice,
    registeredAcnc,
    organisationType,
    authorisedContacts,
    primaryContact
  } = organisationAuthorityHolder;
  const { auditors } = accountant;

  const personalDetails = getOrgApplicationDetails(validationContactInformation, twoFA, organisationPersonalDetail);
  const authorityHolderDetails = getOrgAuthorityHolderDetails(
    organisationAuthorityHolder,
    organisationLicence,
    validationContactInformation,
    twoFA?.twoFaVerified
  );

  let associatedIndividuals = [];
  let associatedOrganisations = [];

  //#region "Individual Trader"

  associatedIndividuals = newTraders
    .filter((x) => x.type.toLowerCase() === commonConsts.OptionsIndividual)
    .map(getNewIndividualTrader);

  //#endregion

  //#region "Organisation Trader"

  associatedOrganisations = newTraders
    .filter((x) => x.type.toLowerCase() === commonConsts.OptionsOrganisation && !x.orgDataInfo?.data?.siebelRowId)
    .map((t) => {
      return getNewOrganisation(t, 'Trader');
    });

  associatedOrganisations = associatedOrganisations.concat(
    newTraders
      .filter((x) => x.type.toLowerCase() === commonConsts.OptionsOrganisation && x.orgDataInfo?.data?.siebelRowId)
      .map((trader) => {
        return {
          id: trader?.orgDataInfo?.data.siebelRowId,
          roles: [],
          roleDetails: [
            {
              RoleType: 'Trader',
              StartDate: trader.startDate
            }
          ]
        };
      })
  );

  //#endregion

  //#region Accountants

  associatedIndividuals = associatedIndividuals.concat(
    auditors
      .filter((x) => x.partyType.toLowerCase() === commonConsts.OptionsIndividual.toLowerCase())
      .map((accountant) => {
        return getNewIndividualAccountant(accountant, organisationLicence?.businessName);
      })
  );

  auditors
    .filter((x) => x.partyType.toLowerCase() === commonConsts.OptionsOrganisation.toLowerCase())
    .map((accountant) => {
      associatedIndividuals = associatedIndividuals.concat(getNewLeadAuditor(accountant));

      associatedOrganisations = associatedOrganisations.concat(getNewOrganisationAccountant(accountant));
    });

  //#endregion

  //#region "Authorised Contacts"

  associatedIndividuals = associatedIndividuals.concat(
    authorisedContacts.filter((x) => x.create === true).map((c) => getNewContactPerson(c, primaryContact))
  );

  //#endregion

  //#region "Organisation Branch"

  associatedOrganisations = associatedOrganisations.concat(
    newBranches
      .filter((x) => x.type.toLowerCase() === commonConsts.OptionsOrganisation && !x.orgDataInfo?.data?.siebelRowId)
      .map((b) => {
        return getNewOrganisation(b, 'Sub-Branch');
      })
  );

  associatedOrganisations = associatedOrganisations.concat(
    newBranches
      .filter((x) => x.type.toLowerCase() === commonConsts.OptionsOrganisation && x.orgDataInfo?.data?.siebelRowId)
      .map((branch) => {
        return {
          id: branch?.orgDataInfo?.data.siebelRowId,
          roles: [],
          roleDetails: [
            {
              RoleType: 'Sub-Branch',
              StartDate: moment(new Date()).format('YYYY-MM-DD')
            }
          ]
        };
      })
  );
  //#endregion

  const isNewAuhorityHolder = !!!orgDataInfo?.data?.siebelRowId;

  // construct premises addresses if auth holder address is used
  const constructNewPremistAddress = (authorityHolderAdresses, exisitngaAthorityHolderAdresses) => {
    let newAddresses = [];

    authorityHolderAdresses.forEach(function (a) {
      let newAddress = a;

      if (!!!a?.details) {
        const existingAuthorityHolderAddress = exisitngaAthorityHolderAdresses.find(
          (ea) => ea.addressType === a.addressType
        );

        if (existingAuthorityHolderAddress && a.addressNotChanged === true) {
          newAddress = {
            addressNotChanged: false,
            addressType: a.addressType,
            details: {
              Status: 'Success',
              Address: {
                addressLine1: existingAuthorityHolderAddress.addressLine1,
                suburb: existingAuthorityHolderAddress.suburb,
                state: existingAuthorityHolderAddress.state,
                postcode: existingAuthorityHolderAddress.postcode,
                country: existingAuthorityHolderAddress.country
              }
            }
          };
          newAddresses.push(newAddress);
        }

        return;
      }

      newAddresses.push(newAddress);
    });

    return newAddresses;
  };

  const authorityHolderAdresses = getMultipleAddressDetail(
    addressRegisteredOfficeValue,
    otherAddresses,
    sameAsRegisteredOffice
  );
  const organisationApplicantName = authorityHolderDetails.authorityHolderName
    ? orgDataInfo?.data?.entityName
    : authorityHolderDetails.registrationName;

  const orgType = !!!organisationLicence?.orgDataInfo?.data?.siebelRowId
    ? authorityHolderDetails.organisationType
    : commonConsts.OrganisationTypes.find((x) => x.value === authorityHolderDetails.organisationType)
    ? authorityHolderDetails.organisationType
    : commonConsts.ORG_TYPE_Other;

  let result = {
    applicationId: application.applicationId,
    licenceType: 'Charitable Fundraising',
    applicationNo: application.applicationNo,
    applicationType: 'new-application',
    applicantType: 'Organisation',
    durationRequested: duration.detail,
    associatedIndividuals: associatedIndividuals,
    associatedOrganisations: associatedOrganisations,
    twoFaVerified: twoFA.data ? true : false,
    fundRaisingDetail: {
      charityObjects: fundraisingDetail.charityObject,
      natureDuration: fundraisingDetail.natureDuration,
      financialYearEnd: `${fundraisingDetail.day}/${fundraisingDetail.month}`,
      directorRemuneration: fundraisingDetail.directorRemuneration,
      applicantOrgType: fundraisingDetail.applicantOrgType,
      charityWebsiteUrl: fundraisingDetail.charityWebsiteUrl,
      //registeredwithACNC: registeredAcnc?.isRegisteredAcnc === true ? 'Y' : 'N'
      registeredwithACNC:
        registeredAcnc?.isRegisteredAcnc !== null
          ? registeredAcnc?.isRegisteredAcnc === true
            ? 'Y'
            : 'N'
          : registeredAcnc?.isRegisteredAcncSelected !== null
          ? registeredAcnc?.isRegisteredAcncSelected === true
            ? 'Y'
            : 'N'
          : null
    },
    organisationApplicantDetails: {
      roles: ['Applicant'],
      id: orgDataInfo?.data?.siebelRowId,
      abn: orgDataInfo?.data?.abn,
      acn: orgDataInfo?.data?.acn,
      regoNumber: authorityHolderDetails.authorityHolderName ? null : authorityHolderDetails.registrationNumber,
      name: organisationApplicantName,
      orgStructure: organisationType,
      addresses: isNewAuhorityHolder ? authorityHolderAdresses : null
    },
    individualApplicantDetails: {
      roles: [],
      roleDetails: [
        {
          RoleType: 'Contact Person',
          StartDate: moment(new Date()).format('YYYY-MM-DD'),
          isPrimary: isPrimaryContact(personalDetails, primaryContact)
        }
      ],
      id: personalDetails.partyRowId,
      twoFaVerified: data ? true : false,
      firstName: personalDetails.firstName,
      middleName: personalDetails.middleName,
      surname: personalDetails.lastName,
      contactPart: {
        emailAddress: personalDetails.emailAddress,
        mobileNo: personalDetails.mobile,
        phoneNo:
          personalDetails.secondaryContact.prefix && personalDetails.secondaryContact.number
            ? `${personalDetails.secondaryContact.prefix}-${personalDetails.secondaryContact.number}`
            : ''
      },
      addresses: getDuplicateAddressDetail(personalDetails?.residentialAddress, personalDetails?.postalAddress)
    },
    attachments: getAllDocuments(supportingDocument, orgType),
    premisesList: [
      {
        businessName: organisationApplicantName,
        id: orgDataInfo?.data?.siebelRowId,
        integrationId: orgDataInfo?.data?.siebelIntegrationId,
        roles: ['Registered Office'],
        premiseAddresses: isNewAuhorityHolder
          ? authorityHolderAdresses
          : constructNewPremistAddress(authorityHolderAdresses, organisationAuthorityHolder.address.addresses)
      }
    ]
  };

  return result;
};

export { formattedOrganisationNewApplicationJson };
